import { createDraftSafeSelector } from '@reduxjs/toolkit';
import { ethers } from 'ethers';

import { Store } from '../../defaultState';
import { configSelector } from '../web3Config/selectors';

import { RequestStatus, MarketStateT, FeesT, InstrumentT } from 'types';

export const selectMarketState = (state: Store): MarketStateT => state.market;

export const instrumentsSelector = (state: Store) => state.market?.instruments;
export const selectedInstrumentSelector = (state: Store) => state.market?.selectedInstrument;
export const instrumentPriceBarsSelector = (state: Store) => state.market?.instrumentPriceBars;

export const marketCurrenciesSelector = createDraftSafeSelector(
  selectMarketState,
  (state: MarketStateT) => state.currencies
);

export const selectTokens = createDraftSafeSelector(configSelector, config => {
  const tokensList = config?.tokens;

  if (tokensList) {
    return tokensList.reduce((acc, token) => {
      return {
        ...acc,
        [token.name]: {
          ...token,
        },
      };
    }, {});
  }

  return null;
});
export const selectContracts = createDraftSafeSelector(configSelector, config => config?.contract);

export const selectSelectedInstrument = createDraftSafeSelector(selectedInstrumentSelector, instrument => instrument);

export const selectInstruments = createDraftSafeSelector(
  instrumentsSelector,
  (instruments: InstrumentT[]) => instruments
);

export const selectInstrumentsPrice = createDraftSafeSelector(selectMarketState, (state: MarketStateT) => state.prices);

export const selectSelectedInstrumentPrice = createDraftSafeSelector(
  selectMarketState,
  (state: MarketStateT) => state.selectedInstrumentPrice
);

export const selectSelectedInstrumentPriceLoading = createDraftSafeSelector(selectMarketState, (state: MarketStateT) =>
  [RequestStatus.None, RequestStatus.Pending].includes(state.selectedInstrumentPriceStatus)
);

export const selectInstrumentPriceBars = createDraftSafeSelector(instrumentPriceBarsSelector, instrumentPriceBars =>
  [...instrumentPriceBars].reverse()
);

export const selectInstrumentPriceBarsLoading = createDraftSafeSelector(selectMarketState, (state: MarketStateT) =>
  [RequestStatus.None, RequestStatus.Pending].includes(state.instrumentPriceBarsStatus)
);

export const selectFees = createDraftSafeSelector(configSelector, (fees): FeesT | null => {
  if (fees) {
    return {
      trading_fee: fees.trading_fee,
      gas_deposit: fees.gas_deposit,
      slippage: fees.slippage,
    };
  }
  return null;
});

export const selectGasPrice = createDraftSafeSelector(configSelector, fees =>
  ethers.utils.parseEther(fees?.gas_deposit || '0')
);

export const selectInstrumentsOptions = createDraftSafeSelector(selectInstruments, (instruments: InstrumentT[]) => {
  return instruments.map(i => ({ label: `${i.base_currency_id} / ${i.quote_currency_id}`, value: i.instrument_id }));
});

export const selectSelectedInstrumentOption = createDraftSafeSelector(
  selectSelectedInstrument,
  (instrument: InstrumentT | null) => {
    if (instrument) {
      return {
        label: `${instrument.base_currency_id} / ${instrument.quote_currency_id}`,
        value: instrument.instrument_id,
      };
    }

    return null;
  }
);
